import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { api, ApiError, forgetDevice } from "./api.ts";

export interface Me {
  user: { id: string; name: string; email?: string; role: "owner" | "admin" | "agent"; sign_in?: string; mobile?: string };
  areas: { name: string; suburbs: number }[];
  passkeys: number;
  device: { id: string; approved: boolean };
  can: { settings: boolean; audit: boolean; imports: boolean; duplicates: boolean; deposits: boolean; privateNotes: boolean; switchRuleToAuto: boolean };
  sending: string;
  google: boolean;
  devOutbox: boolean;
}

const Ctx = createContext<Me | null>(null);
export const useMe = () => useContext(Ctx)!;
export const MeProvider = ({ me, children }: { me: Me; children: ReactNode }) => <Ctx.Provider value={me}>{children}</Ctx.Provider>;

/** null = signed out. */
export function useSession() {
  return useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try { return await api.get<Me>("/me"); } catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
    },
    staleTime: 60_000,
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return async () => {
    try { await api.post("/auth/logout"); } catch { /* signed out anyway */ }
    await forgetDevice();
    // Keep the "me" query itself (the app is watching it) and set it to signed out; drop everything else cached.
    qc.setQueryData(["me"], null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
  };
}
